<template>
  <div>
    <bar-search-categories
      :indexes="indexes"
      :activeIndex="state.index"
    />

    <section class="row">
      <filters v-if="withFilters && !showMobileFilters"
        :state="state"
        :isScopedSearch="isScopedSearch"
        :isResultLoaded="isResultLoaded"
        :isFilterLoaded="isFilterLoaded"
        :isFilterSetLoaded="isFilterSetLoaded"
        :isInitSearchLoaded="isInitSearchLoaded"
        :filterSets="filterSets"
        @setToggled="setFilterSet"
        @bucketToggled="setFilter"
        @bucketAdded="addBucket"
        @bucketShowAll="handleShowAllFilter"
        @filterCollapseToggled="handleCollapseFilter"
        @filterCollapseAllToggled="handleCollapseAllFilters"
        @handleCollapseFilterSection="handleCollapseFilterSection"
        @resetFilters="resetFilters"
        @clearSmartFilterEffects="clearSmartFilterEffects">
      </filters>

      <div id="searchResults" class="search_results " ref="searchResults" :style="[styles]"
        :class="{
          'col-lg-10 offset-lg-1 col-md-12': !withFilters,
          'col-lg-9 col-md-8': withFilters
        }"
      >
        <search-bar :state="state"
          :indexes="indexes"
          :totalPossibleResults="searchResults.total"
          :isIndexesLoaded="isIndexesLoaded"
          :isScopedSearch="isScopedSearch"
          :triggerSearchBarFocus="triggerSearchBarFocus"
          :isResultLoaded="isResultLoaded"
          :withSearchBy="withSearchBy"
          :canSaveResultsToBookmarks="canSaveResultsToBookmarks"
          :isSticky="isSticky"
          @keywordChanged="setKeyword"
          @keywordTyped="setTempKeyword"
          @excludesChanged="setExcludes"
        />

        <filters-mobile v-if="withFilters && showMobileFilters"
          :state="state"
          :isScopedSearch="isScopedSearch"
          :isResultLoaded="isResultLoaded"
          :isFilterLoaded="isFilterLoaded"
          :isFilterSetLoaded="isFilterSetLoaded"
          :isInitSearchLoaded="isInitSearchLoaded"
          :filterSets="filterSets"
          @setToggled="setFilterSet"
          @bucketToggled="setFilter"
          @bucketAdded="addBucket"
          @bucketShowAll="handleShowAllFilter"
          @filterCollapseToggled="handleCollapseFilter"
          @filterCollapseAllToggled="handleCollapseAllFilters"
          @resetFilters="resetFilters"
        />

        <static-messages v-if="!isEmpty(error)"
          :messages="error"
          :variant="`danger`"
          :containerClass="`mb-4`"
          @closed="error = null"
        />

        <div class="position-relative mt-1">
          <!-- render quick filters / selected filters / suggestions -->
          <selected-filters
            :filters="state.filters"
            :isScopedSearch="isScopedSearch"
            :scope="isWithFixedFilter ? isWithFixedFilter.key : ''"
            :isTermSuggestionsLoaded="isTermSuggestionsLoaded"
            :state="state"
            :termSuggestions="termSuggestions"
            :withFilters="withFilters"
            @deactivateFilter="deactivateFilter"
            @setToggled="setFilterSet"
            @resetFilters="resetFilters"
            @setKeyword="setKeyword"
            @termSuggestionChanged="setTermSuggestion"
          />

          <!-- render term suggestions -->
          <term-suggestions v-if="isTermSuggestionsLoaded"
            :state="state"
            :termSuggestions="termSuggestions"
            :isScopedSearch="isScopedSearch"
            @termSuggestionChanged="setTermSuggestion"
          />
        </div>

        <template v-if="isLandscape">
          <landscape
            :config="landscapeConfig"
            :isScopedSearch="isScopedSearch"
            :state="state"
            :count="indexes && indexes[state.index] && indexes[state.index].total || 0"
            :cancelTokenSource="cancelTokenSource"
          />
        </template>
        <template v-else>
          <!-- results to show -->
          <div v-if="isResultLoaded" id="resultsPlaceholder" class="results-placeholder clearfix">
            <categories v-if="!noResultsFound && isResultLoaded"
              :searchResults="searchResults"
              :isScopedSearch="isScopedSearch"
              :itemsPerPage="itemsPerPage"
              :state="state"
              :page="calculatedCurrentPage"
              :showResultCount="enablePaginationHandling"
              :lockedResultPlaceholders="lockedResultPlaceholders"
              :fixedResultComponent="fixedResultComponent"
              :canSaveResultsToBookmarks="canSaveResultsToBookmarks"
              @sortHandled="setSort"
            />

            <!-- pagination -->
            <pagination
              v-if="!lockedResultPlaceholders && enablePaginationHandling"
              :page="calculatedCurrentPage"
              :resultsPerPage="itemsPerPage"
              :totalResults="searchResults.total"
              :maxPages="maxPages"
              showJumpPage
              @changePage="handlePagination"
            />
          </div>

          <search-placeholders v-if="!isResultLoaded"
            :isBlockView="isBlockView"
            class="mt-4"
          />
          <no-result
            v-if="isIndexesLoaded && allResultsRendered && noResultsFound"
            :keyword="state.keyword"
            :index="state.index"
            :indexes="indexes"
            :isEmptyBookmarkFolder="bookmarkFolder && bookmarkFolder.count === 0"
          />

          <freemium-cta v-if="!isScopedSearch && !$can('viewFullSearchResults')" />
        </template>

      </div>
    </section>
  </div>
</template>

<script>
import SearchService from '@/services/search'
import staticData from '@/assets/js/_data'
import container from '@/mixins/search/container'
import helpers from '@/mixins/search/helpers'
import debounce from '@/mixins/debounce'
import axios from 'axios'

import Categories from '@/components/search/results/BaseCategories'
import SearchBar from '@/components/search/Bar'
import NoResult from '@/components/search/NoResult'
import Filters from '@/components/search/Filters'
import BarSearchCategories from '@/components/search/BarSearchCategories'
import SelectedFilters from '@/components/search/SelectedFilter'
import SearchPlaceholders from '@/components/search/Placeholders'
import TermSuggestions from '@/components/search/TermSuggestions'
import StaticMessages from '@/components/shared/StaticMessages'
import FreemiumCta from '@/components/search/FreemiumCTA'
import MPService from '@/services/mixpanel'
import FiltersMobile from '@/components/search/FiltersMobile'
import Pagination from '@/components/search/Pagination'
import Landscape from '@/components/search/Landscape'

import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'searchContainer',
  mixins: [container, helpers],
  components: {
    NoResult,
    Categories,
    Filters,
    SelectedFilters,
    SearchPlaceholders,
    SearchBar,
    StaticMessages,
    FreemiumCta,
    TermSuggestions,
    BarSearchCategories,
    FiltersMobile,
    Pagination,
    Landscape
  },
  props: {
    isWithFixedFilter: {
      type: Object,
      default: () => ({})
    },
    bookmarkFolder: {
      required: false,
      type: Object,
      default: () => null
    },
    isLandscape: {
      required: false,
      default: false,
      type: Boolean
    },
    canSaveResultsToBookmarks: {
      type: Boolean,
      required: false,
      default: true
    },
    // Whether or not the serach bar will have sticky behaviour
    isSticky: {
      type: Boolean,
      required: false,
      default: true
    },
    emptySearchMessage: {
      type: String,
      required: false,
      default: ''
    }
  },
  data () {
    return {
      showMobileFilters: false,
      isResultLoaded: false,
      isFilterLoaded: false,
      isFilterSetLoaded: false,
      isInitSearchLoaded: false,
      isIndexesLoaded: false,
      isTermSuggestionsLoaded: false,
      isScopedSearch: false,
      skipSaveState: false,
      skipScrollToTop: false,
      disableRouteWatcher: false,
      triggerSearchBarFocus: false,
      searchResults: {
        categories: []
      },
      indexes: {},
      itemsPerPage: 12,
      maxPages: 100,
      filterSets: {},
      state: {},
      termSuggestions: [],
      error: '',
      cancelToken: axios.CancelToken,
      cancelTokenSource: '',
      styles: [],
      triggerProductTour: 'product-tour',
      previousKeyword: '',
      previousIndex: '',
      tempKeyword: this.state?.keyword || '',
      landscapeConfig: []
    }
  },
  created () {
    // fetch or build the state
    this.isScopedSearch = !this.isEmpty(this.isWithFixedFilter)
    this.state = this.getState()
    this.setState('global', !this.isScopedSearch)
    this.isScopedSearch ? this.initScopedState() : this.initStateOrQuery()
  },
  mounted () {
    // init catch events
    // when a user edits a checkbox in the browse view
    this.$events.$on('filter:changed:browse', (buckets, filterName) => {
      this.massSetFilter(buckets, filterName)
    })
    // When result gets bookmarked
    this.$events.$on('bookmark_folder:bookmarked', (bookmarkFolders, index, itemID) => {
      if (!this.searchResults.categories || !this.searchResults.categories[index]) return
      const resultIndex = this.searchResults.categories[index].results.findIndex(r => r.data.id === itemID)
      if (resultIndex > -1) {
        const result = Object.assign({}, this.searchResults.categories[index].results[resultIndex].data)
        result.bookmark_folders = bookmarkFolders
        this.$set(this.searchResults.categories[index].results[resultIndex], 'data', result)
      }
    })

    this.$events.$on('search:fetch:scoped_data', (slugValues, index) => this.handleGraph(slugValues, index))
    this.$events.$on('search:fetch:scoped_expertise_data', (slugValues, index) => this.handleExpertise(slugValues, index))
    this.$events.$on('search:changed:saved', (searchId) => this.setSaved(searchId))

    this.$events.$on('search_by_fields:update', (fields) => this.setSearchByFields(fields))

    this.$events.$on('ignore_synonyms:update', (isIgnored) => this.setIgnoreSynonyms(isIgnored))

    this.scrollToTop()
    this.startProductTour()

    window.addEventListener('resize', this.checkWidth)
    this.checkWidth()
  },
  computed: {
    ...mapGetters({
      lastClickedResult: 'search/lastClickedResult'
    }),
    isBlockView () {
      return this.searchView(this.state.index) !== 'list'
    },
    activeFilterSets () {
      if (!this.filterSets || !this.filterSets.buckets) return []
      return this.filterSets.buckets.map(bucket => {
        if (bucket.active) return bucket.value
      }).filter(v => v)
    },
    requestsFinished () {
      return [this.isResultLoaded, this.isFilterLoaded]
    },
    getQueryObj () {
      return Object.assign({}, this.$route.query)
    },
    getParamsObj () {
      return Object.assign({}, this.$route.params)
    },
    noResultsFound () {
      return this.isResultLoaded && (!this.searchResults.total || this.searchResults.total <= 0)
    },
    allResultsRendered () {
      return this.totalResultsRendered >= this.totalPossibleResults
    },
    allSuggestionResultsRendered () {
      return this.totalSuggestionResultsRendered >= this.totalPossibleSuggestionResults
    },
    totalResultsRendered () {
      if (this.isEmpty(this.searchResults.categories[this.state.index])) return 0
      return this.searchResults.categories[this.state.index].results.length
    },
    totalSuggestionResultsRendered () {
      if (this.isEmpty(this.searchResults.categories[this.state.index])) return 0
      return this.searchResults.categories[this.state.index].suggestion_results.length
    },
    totalPossibleResults () {
      if (this.isEmpty(this.searchResults.categories[this.state.index])) return 0
      return this.searchResults.categories[this.state.index].total_results
    },
    totalPossibleSuggestionResults () {
      if (this.isEmpty(this.searchResults.categories[this.state.index])) return 0
      return this.searchResults.categories[this.state.index].total_suggestion_results
    },
    enablePaginationHandling () {
      return this.isResultLoaded && (!this.allResultsRendered || !this.allSuggestionResultsRendered)
    }
  },
  watch: {
    requestsFinished (newVal, oldVal) {
      const [isResult, isFilter] = newVal

      if (this.withFilters) {
        // watcher for our 2 requests
        // get the separated loadedProps we need to check for
        if (this.isEmpty(this.error) && isResult && isFilter) this.handleResponses()
      } else {
        // Don't need to watch filters in this case search is without filters
        if (this.isEmpty(this.error) && isResult) this.handleResponses()
      }
    },
    '$route' (to, from) {
      // disable route watch trigger for blacklisted query params
      for (let key of this.skipRouteWatcherFor) {
        if (to.query.hasOwnProperty(key) || from.query.hasOwnProperty(key)) this.disableRouteWatcher = true
      }
      /*
        to.query.updated refers to a query parameter that forces a new search to happen
        This happens when we run a new search in the same index from within an opened modal search result (publications => LabelList => LabelItem)
        This parameter has no further use and should be ignored in any subsequent action.
      */
      if (to.query.updated || !this.disableRouteWatcher) {
        this.isResultLoaded = false
        this.updateSearchThroughRouteWatch(to, from)
      }
      // set helper variable back on false
      this.disableRouteWatcher = false
    },
    searchView (newVal, oldVal) {
      if (oldVal !== newVal) {
        this.setState('viewport', newVal)
        this.scrollToTop()
        this.saveState()
      }
    }
  },
  methods: {
    ...mapActions('search', [
      'clearLastClickedResult'
    ]),
    checkWidth: debounce(function () {
      // Window resize watcher to check whether to show mobile filters or regular filters
      this.showMobileFilters = window.innerWidth < 900
    }, 0),
    handlePagination (page) {
      this.clearLastClickedResult()
      this.updateURLQuery('page', page)
    },
    initScopedState () {
      let index = (!this.isEmpty(this.$route.params.category) && this.checkForValidCategory(this.$route.params.category))
        ? this.$route.params.category
        : this.isWithFixedFilter.index

      this.setState('index', index)
      this.setState('suggestions', this.isWithFixedFilter.suggestions)
      if (!this.isEmpty(this.isWithFixedFilter.suggestion_indexes)) this.setState('suggestion_indexes', this.isWithFixedFilter.suggestion_indexes)

      const hiddenBucket = {
        active: true,
        label: this.isWithFixedFilter.label
      }
      if (this.isWithFixedFilter.values) hiddenBucket.values = this.isWithFixedFilter.values
      else if (this.isWithFixedFilter.value) hiddenBucket.value = this.isWithFixedFilter.value

      if (this.isWithFixedFilter.scoped_slug_texts) {
        this.setState('scoped_slug_texts', this.isWithFixedFilter.scoped_slug_texts)
      }

      // include hidden filter when not present
      if (!this.state.filter_order.includes(this.isWithFixedFilter.key)) {
        // Add a new hidden bucket
        this.addHiddenBucket(this.isWithFixedFilter.key, hiddenBucket, true)
      } else {
        // Add extra filter to active buckets
        this.$set(this.state.filters[this.isWithFixedFilter.key], 'buckets', [hiddenBucket])
      }

      if (this.isWithFixedFilter.item_type) {
        this.setState('item_type', this.isWithFixedFilter.item_type)
      }

      // initialize state with query params or update query with state params
      this.initStateOrQuery(index)
    },
    initStateOrQuery (index = '') {
      // Override invalid indexes and replace route if needed
      if (
        !this.checkForValidCategory(this.$route.params.category) &&
        !this.checkForValidCategory(index)
      ) {
        this.$flash({
          message: 'Invalid search category'
        }, 'warning')
        this.$router.push({
          name: 'searchHome'
        })
        return
      } else {
        this.initSearchFromQuery(Object.assign({}, this.state.filters), this.isScopedSearch, index)
      }
      this.search()
    },
    getState () {
      const localStorageState = this.$ls.getState()
      const isLocalStorageStateEmpty = this.isEmpty(localStorageState)

      let state = !this.isScopedSearch && !isLocalStorageStateEmpty ? localStorageState : Object.assign({}, staticData.state)
      state.index_changed = true
      if (this.isEmpty(state.preference)) state.preference = !this.isEmpty(this.$currentUser) ? this.$currentUser.slug_values[0] : new Date()
      if (this.isScopedSearch) {
        state.keyword = ''
        state.viewport = localStorageState.viewport || 'block'
      }
      // TODO: Redirect to searchHome if invalid category (check where this function gets called)
      if (!this.checkForValidCategory(state.index)) state.index = 'research_groups'
      if (this.isMobile() && !this.isScopedSearch) {
        state.viewport = 'block'
        state.index = 'research_groups'
      }
      state.per_page = this.itemsPerPage
      return state
    },
    saveState () {
      const scope = this.isScopedSearch ? this.$route.name.split('.')[0] : ''
      this.$ls.setState(this.state, scope)
    },
    setState (key, value) {
      this.$set(this.state, key, value)
    },
    setExcludes (excludes) {
      this.updateURLQuery('keyword', this.tempKeyword, true)
      this.updateURLQuery('exclude', excludes)
    },
    setSearchByFields (fields) {
      this.updateURLQuery('keyword', this.tempKeyword, true)
      this.updateURLQuery('search_by_fields', fields.join(','))
    },
    setIgnoreSynonyms (isIgnored) {
      this.updateURLQuery('ig_s', isIgnored)
    },
    setKeyword (keyword) {
      const cleanedKeyword = this.cleanKeyword(keyword)
      if (cleanedKeyword === this.state.keyword) {
        this.updateURLQuery('exclude', this.state.exclude)
        this.search()
      } else {
        this.setState('term_suggestions', [])
        this.setState('keyword', cleanedKeyword)
        // We want to also apply the excludes (wiithout triggering a new search) when setting keyword
        this.updateURLQuery('exclude', this.state.exclude, true)
        this.updateURLQuery('keyword', cleanedKeyword)
      }
    },
    setTempKeyword (keyword) {
      this.tempKeyword = keyword
    },
    setTermSuggestion (termSuggestions) {
      this.setState('term_suggestions', termSuggestions)
      this.search()
    },
    setSort (sort) {
      this.updateURLQuery('sort', sort)
    },
    setFilterSet (quickFilter) {
      const isActive = quickFilter.active
      const quickFilterValue = quickFilter.value

      if (isActive) {
        MPService.trackFilterSetUsed(quickFilter.value, this.state.index)
      }

      // If we're activating exact_match/exclude_bookmarked, we just set a flag on the state object and do another search
      if (['exact_match', 'exclude_bookmarked'].includes(quickFilter.value)) {
        // Set new value on searchState object
        this.setState(quickFilter.value, isActive)
        // Update quick_filter in the local data (used in filters)
        if (this.filterSets && this.filterSets.buckets) {
          const matchedFilter = this.filterSets.buckets.find(b => b.value === quickFilter.value)
          matchedFilter.active = isActive
        }
        // Update URL to match new request
        this.updateURLParamBuckets()
        return
      }

      // For other quick filters, we toggle all necessary buckets, depending on the isActive flag
      for (const filterName in quickFilter.filters) {
        const stateFilter = this.state.filters[filterName]
        // Expand filter categories to expand, and set loading state to true
        stateFilter.collapsed = false
        stateFilter.isLoading = true

        // For 'since' filters, we can only have 1 value active at the same time. So deactivate any active buckets
        if (quickFilter.filters[filterName].type === 'since') {
          if (isActive && stateFilter.buckets) {
            stateFilter.buckets.forEach(b => {
              b.active = false
            })
          }
        }

        quickFilter.filters[filterName].buckets.forEach(bucket => {
          const stateBucket = stateFilter.buckets && stateFilter.buckets.find(b => {
            return b.value === bucket.value
          })
          // If the bucket is present on the state, update the information on it
          if (stateBucket) {
            // If we're deactivating a bucket
            if (!isActive) {
              if (stateBucket.filter_from_set) {
                // Filter out the active filter sets (if more than 1 applies to the same filter)
                // Only set the bucket to false if all relevant quick filters have been deactivated
                stateBucket.filter_from_set = stateBucket.filter_from_set.filter(f => f !== quickFilterValue)
                if (stateBucket.filter_from_set.length === 0) {
                  stateBucket.filter_from_set = null
                  stateBucket.active = isActive
                }
              } else {
                stateBucket.active = isActive
              }
            } else {
              // Activating a bucket, add the new qfValue to the array of 'filter_from_set'
              stateBucket.active = isActive
              if (stateBucket.filter_from_set) {
                stateBucket.filter_from_set = [...stateBucket.filter_from_set, quickFilterValue]
              } else {
                stateBucket.filter_from_set = [quickFilterValue]
              }
            }
          } else {
            // Create a new bucket and add it to the state
            const newBucket = {
              ...bucket,
              active: isActive,
              filter_from_set: [quickFilterValue]
            }
            if (stateFilter.buckets) {
              stateFilter.buckets.push(newBucket)
            } else {
              stateFilter.buckets = [newBucket]
            }
          }
        })
      }

      // Apply filter order to the state
      if (quickFilter.filter_order) {
        for (const filterName of quickFilter.filter_order) {
          this.urlFilterAndBuckets[filterName] = ''
          if (this.state.filters[filterName].buckets) {
            this.state.filters[filterName].buckets.forEach((b) => {
              if (b.active) this.constructURLParamBucketsArray(filterName, b)
            })

            if (this.isEmpty(this.urlFilterAndBuckets[filterName])) delete this.urlFilterAndBuckets[filterName]
          }
        }
      }

      // Extra expand/scroll/highlight logic for when a filterSet is activated related to other filters
      if (isActive) {
        // Check if we want to expand any additional filters
        if (quickFilter.expand && Array.isArray(quickFilter.expand)) {
          quickFilter.expand.forEach(filterToExpand => {
            const stateFilter = this.state.filters[filterToExpand]
            if (!stateFilter) return
            // Expand filter categories to expand, and set loading state to true
            stateFilter.collapsed = false
            stateFilter.isLoading = true
          })
        }

        // Check if we want to scroll to any active filter
        if (quickFilter.scroll_to) {
          this.setState('scrollToFilter', quickFilter.scroll_to)
        } else {
          delete this.state.scrollToFilter
        }
        // Check if we want to scroll to any active filter
        if (quickFilter.highlight && Array.isArray(quickFilter.highlight)) {
          // We want to expand all highlighted filters by default
          quickFilter.highlight.forEach(filterToExpand => {
            const stateFilter = this.state.filters[filterToExpand]
            if (!stateFilter) return
            // Expand filter categories to expand, and set loading state to true
            stateFilter.collapsed = false
            stateFilter.isLoading = true
          })

          this.setState('highlightFilters', quickFilter.highlight)
        } else {
          delete this.state.highlightFilters
        }

        // If a quickFilter with a default sort got activated, override the state sort
        if (quickFilter.sort) {
          this.setState('sort', quickFilter.sort)
        }
      }

      this.updateURLParamBuckets()
    },
    massSetFilter (buckets, fName) {
      this.urlFilterAndBuckets[fName] = ''
      buckets.forEach(bucket => {
        const existingBucket = this.state.filters[fName].buckets?.find(b => b.value === bucket.value)
        if (existingBucket) {
          existingBucket.active = bucket.active
        } else if (this.state.filters[fName].buckets) {
          this.state.filters[fName].buckets.push(bucket)
        } else {
          this.state.filters[fName].buckets = [bucket]
        }
      })
      this.state.filters[fName].buckets.forEach((b) => {
        if (b.active) this.constructURLParamBucketsArray(fName, b)
      })
      // delete param if nothing is added
      if (this.isEmpty(this.urlFilterAndBuckets[fName])) delete this.urlFilterAndBuckets[fName]
      this.updateURLParamBuckets()
    },
    setFilter: debounce(function (bucket, fName, filters, filterSetToDeactivate = null) {
      if (bucket.active) {
        MPService.trackFilterUsed(fName, this.state.index)
      }
      this.urlFilterAndBuckets[fName] = ''

      // Update filterSets with new active state if needed
      if (bucket.filter_from_set && bucket.filter_from_set.length > 0) {
        if (this.filterSets && this.filterSets.buckets) {
          bucket.filter_from_set.forEach(value => {
            this.filterSets.buckets = this.filterSets.buckets.map(filterSetBucket => {
              if (filterSetBucket.value === value) {
                filterSetBucket.active = bucket.active
              }
              return filterSetBucket
            })
          })
        }
      }
      // Update any other filterSets in the filterSetToDeactivate, these are 'overrides' for specific edge cases like the "Since" filter
      if (filterSetToDeactivate) {
        filterSetToDeactivate.forEach(setToDeactivate => {
          this.filterSets.buckets.forEach(set => {
            if (set.value === setToDeactivate) {
              set.active = false
            }
          })
        })
      }

      // add to bucketParams when bucket is active
      const isHiddenFilter = filters[fName].hidden
      filters[fName].buckets.forEach((b) => {
        if (b.active && !isHiddenFilter) this.constructURLParamBucketsArray(fName, b)
      })

      // delete param if nothing is added
      if (this.isEmpty(this.urlFilterAndBuckets[fName])) delete this.urlFilterAndBuckets[fName]
      this.updateURLParamBuckets()
    }, 0),
    setSaved (searchId) {
      this.setState('saved', searchId)
    },
    deactivateFilter (bucket, filterName, filters) {
      bucket.active = false
      this.setFilter(bucket, filterName, filters)
    },
    handleShowAllFilter (filterName, filter) {
      this.state.filters[filterName].show_all = filter.show_all
      this.saveState()
    },
    handleCollapseFilter (filterName, filter) {
      MPService.trackFilterCollapsed(filterName, filter.collapsed, this.state.index)
      this.skipScrollToTop = true
      this.state.filters[filterName].collapsed = filter.collapsed
      if (!filter.collapsed) this.handleFilters(filterName)
    },
    handleCollapseAllFilters (filters, isCollapsed) {
      // Make sure the fixed filter remains when needed
      if (this.isScopedSearch && this.isWithFixedFilter) {
        if (!filters[this.isWithFixedFilter.key]) {
          filters[this.isWithFixedFilter.key] = this.state.filters[this.isWithFixedFilter.key]
          filters[this.isWithFixedFilter.key].hide = true
        }
      }

      if (this.filterSets && !this.isEmpty(this.filterSets)) {
        this.filterSets.collapsed = isCollapsed
      }

      this.setState('filters', filters)
      if (!isCollapsed) this.handleFilters(true)
    },
    handleCollapseFilterSection (filters, isCollapsed) {
      // Make sure the fixed filter remains when needed
      if (this.isScopedSearch && this.isWithFixedFilter) {
        if (!filters[this.isWithFixedFilter.key]) {
          filters[this.isWithFixedFilter.key] = this.state.filters[this.isWithFixedFilter.key]
          filters[this.isWithFixedFilter.key].hide = true
        }
      }

      this.setState('filters', filters)
      if (!isCollapsed) this.handleFilters(true)
    },
    addBucket (bucket, filterName, isFilterHidden) {
      let filters = this.checkIfFilterIsBuildProperly(Object.assign({}, this.state.filters), filterName, isFilterHidden)
      filters[filterName].buckets.push(bucket)
      this.setFilter(bucket, filterName, filters)
    },
    addHiddenBucket (filterName, bucket, isFilterHidden) {
      let filters = this.checkIfFilterIsBuildProperly(Object.assign({}, this.state.filters), filterName, isFilterHidden)
      let filterOrder = []

      filterOrder.push(filterName)
      filters[filterName].buckets.push(bucket)

      this.setState('filter_order', filterOrder)
      this.setState('filters', filters)
    },
    resetFilters () {
      const queryObject = {}
      const currentSortingState = this.state.sort
      const currentKeyword = this.state.keyword
      const currentExclude = this.state.exclude

      if (currentKeyword) {
        queryObject.keyword = currentKeyword
      }
      if (currentSortingState) {
        queryObject.sort = currentSortingState
      }
      if (currentExclude) {
        queryObject.exclude = currentExclude
      }

      this.triggerSearchBarFocus = !this.triggerSearchBarFocus

      this.pushToRoute({ category: this.state.index }, queryObject, true)
    },
    search () {
      const hasNewHash = this.hasUpdatedSearchHash()
      this.isResultLoaded = false
      this.generateCancelToken()

      if (this.isLandscape) {
        this.handleLandscape()
      } else {
        if (!this.isEmpty(this.state.total_results)) delete this.state.total_results
        this.handleSearch()
      }
      // debugger

      if (hasNewHash) {
        this.handleFilters()
        this.handleTermSuggestions()
        this.handleSynonyms()
        this.handleCounts()
      }
    },
    handleCounts: debounce(function () {
      this.isIndexesLoaded = false
      SearchService.counts(this.state, this.cancelTokenSource.token).then(counts => {
        this.indexes = counts.categories
        this.setState('total_results', counts.categories[this.state.index].total)

        this.isIndexesLoaded = true
      }).catch(err => {
        if (!axios.isCancel(err)) {
          this.isIndexesLoaded = !this.isEmpty(this.error)
          this.error = this.handleError(err)
        }
      })
    }, 0),
    fetchFilterSets: debounce(function () {
      const state = this.state
      // Array of filterSets currently active in the route.
      // Use this to "activate" the required filter sets
      const routeFilterSets = this.$route.query?.qf && this.$can('useFilters') ? this.$route.query.qf.split(',') : []

      // Only have to fetch counts outside of scoped search
      if (this.isScopedSearch) {
        this.isFilterSetLoaded = true
        this.filterSets = {}
        return
      }

      this.isFilterSetLoaded = false
      SearchService.getFilterSets({
        ...state,
        filters: {}, // We don't send a filters object to this request because it messes up the fetching of filter sets. Maybe gets changed in future, check w/ Roy
        filter_order: []
      }, this.cancelTokenSource.token).then(data => {
        // Data is an object composed of filter sets and their
        if (this.isEmpty(data)) {
          this.filterSets = {}
        } else {
          // Check if any sets are new, in order to show the new label or not on the header of quick filters
          const hasNewSets = !!Object.values(data).find(set => set.new)
          this.filterSets = {
            type: 'set',
            hide: false,
            order: 0,
            collapsed: false,
            new: hasNewSets,
            buckets: Object.keys(data).map(setName => {
              data[setName].value = setName
              data[setName].active = routeFilterSets.findIndex(value => setName === value) > -1
              return data[setName]
            }).sort((a, b) => a.order - b.order)
          }
        }

        this.isFilterSetLoaded = true
      }).catch(err => {
        if (!axios.isCancel(err)) {
          this.filterSets = {}
          this.isFilterSetLoaded = true
          this.error = this.handleError(err)
        }
      })
    }, 150),
    handleFilters: debounce(function () {
      this.isFilterLoaded = false

      const indexChanged = (this.state.index !== this.previousIndex)
      const shouldShowFilterWarning = !this.isScopedSearch && !this.$can('useFilters') && !!this.$route.params?.buckets

      // Only fetch filter sets as well if index or query has changed.
      if (indexChanged || (this.state.keyword !== this.previousKeyword)) {
        this.fetchFilterSets()
      }

      this.previousKeyword = this.state.keyword
      this.previousIndex = this.state.index
      SearchService.filters({
        ...this.state,
        index_changed: indexChanged
      }, this.cancelTokenSource.token).then(filters => {
        if (this.isScopedSearch && filters[this.isWithFixedFilter.key]) filters[this.isWithFixedFilter.key].hide = true
        this.setState('filter_order', this.cleanFilterOrder(filters, Object.assign([], this.state.filter_order)))
        this.setState('filters', filters)

        this.isFilterLoaded = true

        // Show warning for users not allowed to use filters, when they attempt to use filters
        if (shouldShowFilterWarning) {
          this.$flash({
            message: `This search is shown without the selected filters. Filters can't be used anymore in the Free version.`,
            cta: {
              text: 'More info',
              action: () => { this.$can.notAllowed() }
            },
            sticky: false
          }, 'warning')
        }
      }).catch(err => {
        if (!axios.isCancel(err)) {
          this.isFilterLoaded = !this.isEmpty(err)
          this.error = this.handleError(err)
        }
      })
    }, 50),
    handleTermSuggestions () {
      this.isTermSuggestionsLoaded = false
      if (!this.state.keyword) {
        this.termSuggestions = []
        this.setState('term_suggestions', [])
        return
      }
      SearchService.termSuggestions(this.state, this.cancelTokenSource.token).then(termSuggestions => {
        this.termSuggestions = termSuggestions
        if (this.state.term_suggestions) {
          // Check if there are suggestions present in state, but not returned from ES
          const missingSuggestions = []
          this.state.term_suggestions.forEach(term => {
            // Try to find state suggestions in suggestions returned from ES
            const t = termSuggestions.find(termSuggestion => {
              return termSuggestion.value === term
            })
            // If ES suggestions is missing, add the state suggestion to the array of missing suggestions
            if (!t) {
              missingSuggestions.push({
                value: term,
                active: true,
                count: 1
              })
            }
          })
          // Merge term suggestions with missing suggestions
          this.termSuggestions.push(...missingSuggestions)
        }
        this.isTermSuggestionsLoaded = true
      }).catch(err => {
        if (!axios.isCancel(err)) {
          this.isTermSuggestionsLoaded = !this.isEmpty(this.error)
          this.error = this.handleError(err)
        }
      })
    },
    handleSynonyms: debounce(function () {
      // No need to fetch synonyms if we don't have a keyword, just erase them
      if (!this.state.keyword) {
        this.setState('synonyms', {})
        return
      }
      SearchService.synonyms(this.state).then(synonyms => {
        this.setState('synonyms', synonyms)
      }).catch(err => {
        if (!axios.isCancel(err)) {
          this.error = this.handleError(err)
        }
      })
    }, 150),
    handleSearch () {
      SearchService.search(this.state, this.cancelTokenSource.token).then(r => {
        this.searchResults = Object.assign({}, r)

        MPService.trackSearch(this.state.keyword, this.state.index)

        this.setState('saved', r.saved)
        // Store active sorting on search object
        const activeSort = r.categories[this.state.index].sorts.find(s => s.active)
        if (activeSort) this.setState('sort', activeSort.name)

        this.setState('index_changed', false)
        this.isResultLoaded = true
      }).catch(err => {
        if (!axios.isCancel(err)) {
          this.isResultLoaded = !this.isEmpty(err)
          this.error = this.handleError(err)
        }
      })
    },
    handleLandscape () {
      this.landscapeConfig = []
      SearchService.getDashboardConfiguration(this.state.index).then(r => {
        this.landscapeConfig = r
        this.isResultLoaded = true
      }).catch(err => {
        if (!axios.isCancel(err)) {
          this.isResultLoaded = !this.isEmpty(err)
          this.error = this.handleError(err)
        }
      })
    },
    handleResponses () {
      if (!this.skipSaveState) this.saveState()

      if (!this.skipScrollToTop) {
        // Scroll to clicked item if there is one in Vuex store and we're not in a scoped search
        if (!this.isScopedSearch && this.lastClickedResult.id) {
          this.scrollToClickedItem()
        } else {
          // Regular scroll to top on index/query/filter change
          this.scrollToTop()
        }
      }

      this.skipSaveState = this.skipScrollToTop = false
      this.isInitSearchLoaded = true
      this.checkToRemoveProductTour()
      this.$Progress.finish()
    },
    handleGraph (slugValues, index) {
      SearchService.graphData(this.getSearchObjectGraphDataForSearchCard('publications', index, slugValues, this.state)).then(graphData => {
        this.$events.$emit(`search:send:scoped_data:${index}:${slugValues[0]}`, graphData)
      }).catch(err => {
        if (!axios.isCancel(err)) {
          this.$events.$emit(`search:error:scoped_data:${index}:${slugValues[0]}`, err)
        }
      })
    },
    handleExpertise (slugValues, index) {
      SearchService.expertiseData(this.getSearchObjectGraphDataForSearchCard('publications', index, slugValues, this.state)).then(expertiseData => {
        this.$events.$emit(`search:send:scoped_expertise_data:${index}:${slugValues[0]}`, expertiseData)
      }).catch(err => {
        if (!axios.isCancel(err)) {
          this.$events.$emit(`search:error:scoped_expertise_data:${index}:${slugValues[0]}`, err)
        }
      })
    },
    startProductTour () {
      if (this.getQueryObj.hasOwnProperty(this.triggerProductTour)) this.$alIntercom.startTourOnSearch()
    },
    checkToRemoveProductTour () {
      this.$nextTick(() => {
        if (this.getQueryObj.hasOwnProperty(this.triggerProductTour)) {
          this.resetURLQueryByValue(this.getQueryObj, this.triggerProductTour)
          this.replaceToRoute(this.getParamsObj, this.getQueryObj)
        }
      })
    },
    generateCancelToken () {
      if (!this.isEmpty(this.cancelTokenSource)) { this.cancelTokenSource.cancel('User navigated to different page') }
      this.cancelTokenSource = this.cancelToken.source()
    },
    scrollToTop () {
      if (this.isLandscape) return

      this.$nextTick(() => {
        const el = document.querySelector('#searchResults')
        if (!el) return
        if (window.scrollY > el.offsetTop - 100) {
          this.$scrollTo(`#searchResults`, 150, { easing: 'ease-in', offset: -100 })
        }
      })
    },
    scrollToClickedItem () {
      this.$nextTick(() => {
        const clickedItem = document.getElementById(this.lastClickedResult.id)
        if (clickedItem) {
          this.$scrollTo(`#${this.lastClickedResult.id}`, 150, { easing: 'ease-in', offset: -160 })
        }
        this.clearLastClickedResult() // clear search result url after use
      })
    },
    setDisableRouteWatcherOnTrue (from) {
      this.disableRouteWatcher = true
    },
    clearSmartFilterEffects () {
      delete this.state.scrollToFilter
      delete this.state.highlightFilters
    }
  },
  destroyed () {
    this.$events.$off('bookmark_folder:bookmarked')
    this.$events.$off('search:fetch:scoped_data')
    this.$events.$off('search:fetch:scoped_expertise_data')
    this.$events.$off('filter:changed:browse')
    this.$events.$off('search_by_fields:update')
    this.$events.$off('ignore_synonyms:update')
    window.removeEventListener('resize', this.checkWidth)

    if (!this.isScopedSearch) {
      this.$events.$off('search:changed:saved')
    }
  }
}
</script>
